import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'model/Api';
import { useAuth } from 'model/Auth';
import { User } from 'model/User';
import { useRole } from 'model/Role';
import { Partner } from 'model/Partner';
import { Ledger } from 'model/Ledger';
import {
  Report,
  ReportMemberUser,
  ReportMemberPartner,
  reportContractTypeList,
  ReportWorkResource,
  reportMemberStatusList,
} from 'model/Report';
import { WorkProcess } from 'model/WorkProcess';
import { WorkResource } from 'model/WorkResource';
import { ResourceParam } from 'model/Resource';
import { sortType } from 'context/DataStoreContext';
import { mediaQurey, generalStyle } from 'helper/Style';
import { convertDateCode, convertUserName } from 'helper/Converter';
import { useScroll } from 'helper/Anchor';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import Select from 'component/part/Select';
import SelectDate from 'component/part/SelectDate';
import SelectTime from 'component/part/SelectTime';
import RadioList from 'component/part/RadioList';
import H2 from 'component/part/H2';
import ConfirmResultModal from 'component/part/ConfirmResultModal';
import SelectUser from 'component/part/SelectUser';
import Textarea from 'component/part/Textarea';
import ErrorMessage from 'component/part/ErrorMessage';

type MemberAttribute = {
  inTime: { hour: number; minute: number; };
  outTime: { hour: number; minute: number; };
  setInTime: boolean;
  setOutTime: boolean;
  isGuest?: boolean;
};

interface MemberUserAttribute extends ReportMemberUser, MemberAttribute {};
interface MemberPartnerAttribute extends ReportMemberPartner, MemberAttribute {};
interface ReportWithAttributes extends Report {
  ledgerName: Ledger['name'];
  memberUsers: MemberUserAttribute[];
  memberPartners: MemberPartnerAttribute[];
};

type ReportMachineryType = 'machinery' | 'att' | 'car';
type ReportWorkResourceType = Exclude<WorkResource['type'], ReportMachineryType>;

type ReportWorkResourceGroups = {
  [type in ReportWorkResourceType]: {
    group: number;
    list: (WorkResource & ReportWorkResource)[];
  }[];
};

const reportWorkResourceTypes: ReportWorkResourceType[] = ['person', 'tool', 'car_related', 'industrial_waste', 'other'];

const makeInitialReportWorkResourceGroups = () =>
  reportWorkResourceTypes.reduce((groups, type) => ({ ...groups, [type]: [] }), {}) as ReportWorkResourceGroups;

type ReportMachineGroups = {
  type: ReportMachineryType;
  list: (WorkResource & ReportWorkResource)[];
  selectedList: (WorkResource & ReportWorkResource)[];
}[];

const machineryLabel = {
  machinery: '重機',
  att: 'ATT',
  car: '車両',
};

type UploadResourceParam = { file: File, thumbnail?: string, summary: string };
type UploadedResourceParam = ResourceParam & { delete: boolean };

const partnerCandidates = Array(21).fill(null).map((_, index) => ({ value: index, label: `${index}人` }));
const toolCandidates = Array(21).fill(null).map((_, index) => ({ value: index }));
const machineryCandidates = Array(20).fill(null).map((_, index) => ({ value: index + 1 }));

const resourceUrl = process.env.REACT_APP_RESOURCE_ENDPOINT || '';

const style = {
  layout: {
    display: 'grid',
    width: 'fit-content',
    fontSize: '1.6rem',
    [mediaQurey('min', 'pc')]: {
      gridTemplateColumns: 'auto auto',
      gap: '4rem',
    },
    [mediaQurey('max', 'tablet')]: {
      gridTemplateRows: 'auto auto',
      gap: '2rem',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  dateArea: {
    display: 'flex',
    gap: '2rem 4rem',
    [mediaQurey('max', 'tablet')]: {
      flexWrap: 'wrap' as const,
    },
  },
  timeArea: {
    [mediaQurey('min', 'pc')]: {
      display: 'contents',
    },
    [mediaQurey('max', 'tablet')]: {
      display: 'flex',
      gap: '4rem',
    },
  },
  informationArea: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2rem',
  },
  informationLabel: {
    color: '#888888',
  },
  informationValue: {
  },
  memberOperation: {
    [mediaQurey('min', 'pc')]: {
      display: 'grid',
      gridTemplateColumns: '1fr auto auto auto',
      gap: '4rem',
    },
    [mediaQurey('max', 'tablet')]: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '2rem',
      paddingBottom: '2rem',
      borderBottom: 'solid 0.2rem #cccccc',
    },
    ['& input[type="radio"]']: {
      marginRight: '0.2rem',
    },
  },
  memberOperationPart: {
    [mediaQurey('min', 'pc')]: {
      display: 'contents',
    },
    [mediaQurey('max', 'tablet')]: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '2rem 4rem',
      ['&:nth-of-type(1) > div:nth-of-type(1)']: {
        flexGrow: '1',
      },
    },
  },
  memberSelect: {
    ['& + div']: {
      maxWidth: 'calc(100vw - 16rem)',
    },
  },
  machineryOperation: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'end',
    gap: '4rem',
  },
  machinerySelect: {
    [mediaQurey('max', 'tablet')]: {
      flex: '1',
      maxWidth: '100%',
      ['& [data-part="options"]']: {
        maxWidth: '70rem',
        ['& > div']: {
          whiteSpace: 'wrap',
        },
      }
    },
    [mediaQurey('max', 'sp')]: {
      flex: '1',
      maxWidth: '100%',
      ['& [data-part="options"]']: {
        maxWidth: '20rem',
        ['& > div']: {
          whiteSpace: 'wrap',
        },
      }
    },
  },
  inputArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  option: {
    width: '100%',
    minHeight: '16rem',
    padding: '1rem',
  },
  workResourceArea: {
    ...generalStyle.formRow,
    flexWrap: 'wrap' as const,
  },
  workResourceItemArea: (valueType: WorkResource['valueType']) => ({
    count: {
      [mediaQurey('min', 'pc')]: {
        width: 'calc(25% - 1.5rem)',
      },
      [mediaQurey('max', 'tablet')]: {
        width: 'calc(33% - 1.4rem)',
      },
    },
    volume: {
      [mediaQurey('min', 'pc')]: {
        width: 'calc(25% - 1.5rem)',
      },
      [mediaQurey('max', 'tablet')]: {
        width: 'calc(33% - 1.4rem)',
      },
    },
    text: {
      width: 'calc(50% - 1rem) !important',
    },
    free_text: {
      width: '100%',
    }
  }[valueType]),
  workResourceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  resourceArea: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2rem',
  },
  resourceItem: (deleted = false) => ({
    display: 'grid',
    gridTemplateColumns: 'auto 1fr 10rem',
    gridTemplateRows: 'auto auto',
    gap: '0.5rem',
    alignItems: 'center',
    border: 'solid 0.1rem #cccccc',
    borderRadius: '0.5rem',
    padding: '1rem',
    ['& > input,& > div:nth-of-type(2)']: {
      gridColumn: '1 / 3',
      gridRow: '2',
    },
    ['& > img']: {
      gridColumn: '3',
      gridRow: '1 / 3',
      maxWidth: '10rem',
      maxHeight: '10rem',
    },
    ...(deleted ? {
      backgroundColor: '#ddddff',
    } : {}),
  }),
  uploadedResourceArea: (display: boolean) => ({
    display: 'grid',
    transition: 'grid-template-rows 0.3s',
    gridTemplateRows: `2rem ${display ? 1 : 0}fr`,
    cursor: 'pointer',
    ['& > div:nth-of-type(1)::after']: {
      content: `"${display ? '▲' : '▼'}"`,
      marginLeft: '0.2rem',
    },
    ['& > div:nth-of-type(2)']: {
      overflowY: 'hidden' as const,
    },
  }),
  addButton: (deleted = false) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'fit-content',
    padding: '0 1rem',
    height: '3rem',
    borderRadius: '0.3rem',
    cursor: 'pointer',
    ...(deleted ? {
      backgroundColor: '#6666aa',
      border: 'solid 0.1rem #444488',
      color: '#ffffff',
      ['&:hover']: {
        backgroundColor: '#8888cc',
      },
    } : {
      backgroundColor: '#eeeeee',
      border: 'solid 0.1rem #aaaaaa',
      ['&:hover']: {
        backgroundColor: '#cccccc',
      },
    }),
  }),
};

type WorkResourceGroupFormProps = {
  groups: ReportWorkResourceGroups;
  groupCode: ReportWorkResourceType;
  onChange: <K extends 'value' | 'text'>(workResource: WorkResource, key: K, value: ReportWorkResource[K]) => void;
};

const WorkResourceGroupForm = (props: WorkResourceGroupFormProps) =>
  <Fragment>
    {props.groups[props.groupCode].map((workResourceGroup, groupIndex) => (
      <div css={style.workResourceArea} key={groupIndex}>
        {workResourceGroup.list.map((workResource, index) => (
          <FormContent label={workResource.name} css={style.workResourceItemArea(workResource.valueType)} key={index}>
            {(
              workResource.valueType === 'count' ? (
                <div css={style.workResourceItem}>
                  <Select
                    list={toolCandidates}
                    value={toolCandidates.find((item) => item.value === workResource.value)}
                    labelKey='value'
                    onChange={(item) => props.onChange(workResource, 'value', item?.value)}
                  />
                  <span>{workResource.unitName}</span>
                </div>
              ) :
              workResource.valueType === 'volume' ? (
                <div css={style.workResourceItem}>
                  <input
                    type='number'
                    value={workResource.value}
                    onChange={(event) => props.onChange(workResource, 'value', parseInt(event.target.value))}
                  />
                  <span>{workResource.unitName}</span>
                </div>
              ) :
              workResource.valueType === 'text' ? (
                <input
                  type='text'
                  value={workResource.text}
                  onChange={(event) => props.onChange(workResource, 'text', event.target.value)}
                />
              ) : (
                <Textarea
                  initialValue={workResource.text}
                  identifier={props.groups}
                  onChange={(value) => props.onChange(workResource, 'text', value)}
                  placeholder={workResource.name}
                  css={style.option}
                />
              )
            )}
          </FormContent>
        ))}
      </div>
    ))}
  </Fragment>;

const ReportEdit: React.FC = () => {
  const { user: owner } = useAuth();
  const api = useApi();
  const { getListAsync: getRoleListAsync } = useRole();
  const navigate = useNavigate();
  const urlParams = useParams<{ date: string, id: string }>();
  const initialDateCode = parseInt(urlParams.date || '');
  const initialReportId = parseInt(urlParams.id || '');
  const scroll = useScroll({ scrollKey: 'index', smooth: true });
  const [dateCode, setDateCode] = useState<number>(initialDateCode);
  const [targetDate, setTargetDate] = useState<Date | null>();
  const [reports, setReports] = useState<ReportWithAttributes[]>([]);
  const [reportIndex, setReportIndex] = useState<number>(-1);
  const [minDate, setMinDate] = useState<Date>();
  const [startTime, setStartTime] = useState<{ hour: number, minute: number }>({ hour: 8, minute: 0 });
  const [endTime, setEndTime] = useState<{ hour: number, minute: number }>({ hour: 17, minute: 0 });
  const [users, setUsers] = useState<User[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [uploadResources, setUploadResources] = useState<UploadResourceParam[]>([]);
  const [uploadedResources, setUploadedResources] = useState<UploadedResourceParam[]>([]);
  const [displayUploadedResources, setDisplayUploadedResources] = useState(false);
  const [workProcesses, setWorkProcesses] = useState<WorkProcess[]>([]);
  const [workResources, setWorkResources] = useState<WorkResource[]>([]);
  const [workResourceGroups, setWorkResourceGroups] = useState<ReportWorkResourceGroups>(makeInitialReportWorkResourceGroups());
  const [machineryGroups, setMachineryGroups] = useState<ReportMachineGroups>([]);
  const [error, setError] = useState<{ [key: string]: string | null }>({});
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const report = reports[reportIndex];
  const workProcess = workProcesses.find(({ id }) => id === report?.workProcessId) || workProcesses[0];
  const contractType = reportContractTypeList.find(({ value }) => value === report?.contractType) || reportContractTypeList[1];
  const resourceInputRef = useRef<HTMLInputElement>(null);
  const isProcessingRef = useRef(false);
  const guestUserCandidates = users.filter((p) => !report?.memberUsers.find((mu) => mu.memberId === p.id));
  const guestPartnerCandidates = partners.filter((p) => !report?.memberPartners.find((mu) => mu.memberId === p.id));
  const reportMachineryGroups = machineryGroups.map(
    (group) => ({
      ...group,
      candidateList: group.list.filter(({ value }) => !value),
    })
  );

  const onChangeTargetDate = (changeDate: Date | null) => {
    setTargetDate(changeDate);
    if (changeDate) {
      const changeDateCode = convertDateCode(changeDate);
      setDateCode(changeDateCode);
      window.history.pushState(null, '', `/report/${changeDateCode}/create`);
    }
  };

  const onSelectReport = (report: ReportWithAttributes, changeIndex = true) => {
    const reportStartDateTime = report.startDatetime && new Date(report.startDatetime * 1000);
    const reportEndDateTime = report.endDatetime && new Date(report.endDatetime * 1000);
    if (changeIndex) {
      setReportIndex(reports.findIndex(({ ledgerId }) => ledgerId === report.ledgerId));
    }
    reportStartDateTime && setStartTime({ hour: reportStartDateTime.getHours(), minute: reportStartDateTime.getMinutes() });
    reportEndDateTime && setEndTime({ hour: reportEndDateTime.getHours(), minute: reportEndDateTime.getMinutes() });
    setUploadedResources((report.resources || []).map((resource) => ({ ...resource, delete: false })));
  };

  const onChangeReport = <T extends keyof ReportWithAttributes>(key: T, value: ReportWithAttributes[T]) => {
    if (report) {
      report[key] = value;
      setReports([...reports]);
      setError({ ...error, [key]: null });
    }
  };

  const onChangeStartTime = async (type: 'hour' | 'minute', value: number) => {
    setStartTime({ ...startTime, [type]: value });
    setError({ ...error, time: null });
  };

  const onChangeEndTime = async (type: 'hour' | 'minute', value: number) => {
    setEndTime({ ...endTime, [type]: value });
    setError({ ...error, time: null });
  };

  const onChangeMemberUser = <T extends keyof MemberUserAttribute>(user: MemberUserAttribute, key: T, value: MemberUserAttribute[T]) => {
    user[key] = value;
    setReports([...reports]);
  };

  const onChangeMemberPartner = <T extends keyof MemberPartnerAttribute>(partner: MemberPartnerAttribute, key: T, value: MemberPartnerAttribute[T]) => {
    partner[key] = value;
    setReports([...reports]);
  };

  const onAddGuestUser = () => {
    if (report) {
      const minId = Math.min(...report.memberUsers.map(({ id }) => id), 0) - 1;
      report.memberUsers.push({
        id: minId,
        reportId: report.id,
        memberId: 0,
        lastName: '',
        firstName: '',
        status: 1,
        inDatetime: 0,
        outDatetime: 0,
        inTime: { ...startTime },
        outTime: { ...endTime },
        setInTime: false,
        setOutTime: false,
        isGuest: true,
      });
      setReports([...reports]);
    }
  };

  const onChangeGuestUser = (index: number, user: User | MemberUserAttribute) => {
    const memberUser = report?.memberUsers[index];
    if (report && memberUser?.isGuest) {
      memberUser.id = 0;
      memberUser.memberId = 'memberId' in user ? user.memberId : user.id;
      memberUser.lastName = user.lastName;
      memberUser.firstName = user.firstName;
      setReports([...reports]);
    }
  };

  const onRemoveGuestUser = (index: number) => {
    const memberUser = report?.memberUsers[index];
    if (report && memberUser?.isGuest) {
      report.memberUsers.splice(index, 1);
      setReports([...reports]);
    }
  };

  const onAddGuestPartner = () => {
    if (report) {
      const minId = Math.min(...report.memberPartners.map(({ id }) => id), 0) - 1;
      report.memberPartners.push({
        id: minId,
        reportId: report.id,
        memberId: 0,
        name: '',
        status: 1,
        inDatetime: 0,
        outDatetime: 0,
        count: 1,
        inTime: { ...startTime },
        outTime: { ...endTime },
        setInTime: false,
        setOutTime: false,
        isGuest: true,
      });
      setReports([...reports]);
    }
  };

  const onChangeGuestPartner = (index: number, partner: Partner | MemberPartnerAttribute) => {
    const memberPartner = report?.memberPartners[index];
    if (report && memberPartner?.isGuest) {
      memberPartner.id = 0;
      memberPartner.memberId = 'memberId' in partner ? partner.memberId : partner.id;
      memberPartner.name = partner.name;
      setReports([...reports]);
    }
  };

  const onRemoveGuestPartner = (index: number) => {
    const memberPartner = report?.memberPartners[index];
    if (report && memberPartner?.isGuest) {
      report.memberPartners.splice(index, 1);
      setReports([...reports]);
    }
  };

  const onChangeWorkResource = <K extends 'value' | 'text'>(workResource: ReportWorkResource, key: K, value: ReportWorkResource[K]) => {
    workResource[key] = value;
    setWorkResourceGroups({ ...workResourceGroups });
  };

  const changeUploadResource = (changedResourceParams: UploadResourceParam[]) => {
    const resourceData = new DataTransfer();
    changedResourceParams.forEach((resourceParam) => {
      resourceData.items.add(resourceParam.file);
    });
    if (resourceInputRef.current) {
      resourceInputRef.current.files = resourceData.files;
    }
    setUploadResources([...changedResourceParams]);
  };

  const onAddMachinery = (type: ReportMachineryType) => {
    const machineryGroup = machineryGroups.find((item) => item.type === type);
    machineryGroup?.selectedList.push({
      id: 0,
      clientId: 0,
      type,
      valueType: 'count',
      name: '',
      group: 1,
      order: 1,
      value: 1,
    });
    setMachineryGroups([...machineryGroups]);
  };

  const onChangeMachinery = (type: ReportMachineryType, index: number, id: number) => {
    const machineryGroup = machineryGroups.find((item) => item.type === type);
    const machinery = machineryGroup?.selectedList[index];
    if (machinery) {
      const changedMachinery = machineryGroup?.list.find((item) => item.id === id);
      if (changedMachinery) {
        changedMachinery.value = machinery.value;
        machinery.value = 0;
        machineryGroup.selectedList.splice(index, 1);
        machineryGroup.selectedList.splice(index, 0, changedMachinery);
      }
      setMachineryGroups([...machineryGroups]);
    }
  };

  const onChangeMachineryValue = (machinery: WorkResource & ReportWorkResource, value: number) => {
    machinery.value = value;
    setMachineryGroups([...machineryGroups]);
  };

  const onRemoveMachinery = (type: ReportMachineryType, index: number) => {
    const machineryGroup = machineryGroups.find((item) => item.type === type);
    const machinery = machineryGroup?.selectedList[index];
    if (machinery) {
      machinery.value = 0;
      machineryGroup.selectedList.splice(index, 1);
      setMachineryGroups([...machineryGroups]);
    }
  };

  const onAddUploadResource = (event: React.ChangeEvent<HTMLInputElement>) => {
    Array.from(event.target.files || []).forEach((resource) => {
      if (!uploadResources.find(({ file: { name } }) => name === resource.name)) {
        const resourceParam: UploadResourceParam = { file: resource, summary: '' };
        uploadResources.push(resourceParam);
        const reader = new FileReader();
        reader.addEventListener('load', (loadedEvent) => {
          const thumbnail = loadedEvent?.target?.result;
          if (thumbnail) {
            resourceParam.thumbnail = thumbnail as string;
            setUploadResources(uploadResources);
          }
        });
        reader.readAsDataURL(resource);
      }
    });
    changeUploadResource(uploadResources);
  };

  const onDeleteUploadReource = (index: number) => {
    uploadResources.splice(index, 1);
    changeUploadResource(uploadResources);
  };

  const changeUploadResourceSummary = (index: number, summary: string) => {
    if (uploadResources.length > index) {
      uploadResources[index].summary = summary;
      setUploadResources([...uploadResources]);
    }
  };

  const onDeleteUploadedReource = (index: number) => {
    if (uploadedResources.length > index) {
      uploadedResources[index].delete = !uploadedResources[index].delete;
      setUploadedResources([...uploadedResources]);
    }
  };

  const getTimestamps = () => {
    if (!report) return null;
    const startDateTime = new Date(report.startDatetime * 1000);
    const endDateTime = new Date(report.startDatetime * 1000);
    startDateTime.setHours(startTime.hour);
    startDateTime.setMinutes(startTime.minute);
    startDateTime.setSeconds(0);
    endDateTime.setHours(endTime.hour);
    endDateTime.setMinutes(endTime.minute);
    endDateTime.setSeconds(0);

    return {
      startTimestamp: Math.floor(startDateTime.getTime() / 1000),
      endTimestamp: Math.floor(endDateTime.getTime() / 1000),
      startDateTime,
      endDateTime,
    };
  };

  const validateForSubmit = (): boolean => {
    if (!report) return false;
    const { memberUsers: _m, memberPartners: _p, ...reportParam } = report;
    const timestamps = getTimestamps();
    const errors: [string, string][] = [];
    if (!timestamps || !timestamps.startTimestamp || timestamps.startTimestamp > timestamps.endTimestamp) {
      errors.push(['time', '作業時間を正しく指定してください。']);
    }
    if (!reportParam.contractType) {
      errors.push(['contractType', '現場種別を選択してください。']);
    }
    if (!reportParam.workProcessId) {
      errors.push(['workProcessId', '作業内容を選択してください。']);
    }
    if (!uploadedResources.length && !uploadResources.length) {
      errors.push(['resource', '作業写真を1枚以上アップロードしてください。']);
    }
    if (errors.length) {
      errors.forEach(([key, message]) => { error[key] = message; });
      setError({ ...error });
      scroll(errors[0][0]);

      return false;
    }

    return true;
  };

  const doApiUpdate = async (fixed: boolean): Promise<boolean> => {
    if (!report) return false;
    if (isProcessingRef.current) return false;
    isProcessingRef.current = true;
    const { memberUsers, memberPartners, ...reportParam } = report;
    const timestamps = getTimestamps();
    if (!timestamps) {
      isProcessingRef.current = false;
      return false;
    }
    const { startTimestamp, endTimestamp, startDateTime, endDateTime } = timestamps;

    [memberUsers, memberPartners].forEach((members) => {
      members.forEach((member) => {
        member.inDatetime = null;
        member.outDatetime = null;
        if (member.status) {
          if (member.setInTime) {
            const inDatetime = new Date(startDateTime);
            inDatetime.setHours(member.inTime.hour);
            inDatetime.setMinutes(member.inTime.minute);
            member.inDatetime = Math.floor(inDatetime.getTime() / 1000);
          }
          if (member.setOutTime) {
            const outDatetime = new Date(endDateTime);
            outDatetime.setHours(member.outTime.hour);
            outDatetime.setMinutes(member.outTime.minute);
            member.outDatetime = Math.floor(outDatetime.getTime() / 1000);
          }
        }
      });
    });

    const reportWorkResources: ReportWorkResource[] = [];
    reportWorkResourceTypes.forEach((workResourceType) => {
      workResourceGroups[workResourceType].forEach(({ list }) => {
        const selectedWorkResources = list.flatMap(
          ({ id, value, text }) => (id && (value || text))
            ? { id, ...(value ? { value } : {}), ...(text ? { text } : {}) }
            : []
        );
        reportWorkResources.push(...selectedWorkResources);
      });
    });
    reportMachineryGroups.forEach(({ selectedList }) => {
      const selectedWorkResources = selectedList.flatMap(
        (item) => item.id ? { id: item.id, value: item.value } : []
      );
      reportWorkResources.push(...selectedWorkResources);
    });

    const apiResult = await api.report.update(
      {
        ...reportParam,
        startDatetime: startTimestamp,
        endDatetime: endTimestamp,
        workResources: reportWorkResources,
        fixed,
      },
      memberUsers,
      memberPartners,
      uploadResources,
      uploadedResources.flatMap((item) => item.delete ? item.indexName : []),
    );
    if (!apiResult.ok) {
      if (apiResult.apiError.error === 'duplicate_report') {
        // 既存レコードが見つかった場合は編集モードに切り替える
        report.id = apiResult.apiError.existingId;
        setReports(reports);
        // 編集モードで再試行
        isProcessingRef.current = false;
        return doApiUpdate(fixed);
      }
      isProcessingRef.current = false;
      return false;
    }
    if (apiResult.data?.id) {
      report.id = apiResult.data.id;
      setReports(reports);
    }
    isProcessingRef.current = false;

    return true;
  };

  const onSave = async () => doApiUpdate(false);

  const onClickSubmitButton = () => {
    if (validateForSubmit()) {
      setShowSubmitConfirm(true);
    }
  };

  const onSubmitReport = async () => doApiUpdate(true);

  useEffect(() => {
    (async () => {
      const [
        usersResponse,
        partnersResponse,
        workProcessesResponse,
        workResourceResponse,
        roles,
      ] = await Promise.all([
        api.user.getsByOwn(),
        api.partner.getsByOwn(),
        api.workProcess.getsByOwn(),
        api.workResource.getsByOwn(),
        getRoleListAsync(),
      ]);
      const assignTargetRoleIds = roles.flatMap((role) => role.permissions.workAssign ? role.id : []);
      setUsers(usersResponse.filter(({ roleId }) => assignTargetRoleIds.includes(roleId)));
      setPartners(partnersResponse);
      setWorkProcesses(workProcessesResponse);
      setWorkResources(workResourceResponse);
    })();
  }, []);

  useEffect(() => {
    const reportWorkResources = report?.workResources || [];
    const reportWorkResourceGroups = makeInitialReportWorkResourceGroups();
    const reportMachineryGroups: ReportMachineGroups = [
      { type: 'machinery', list: [], selectedList: [] },
      { type: 'att', list: [], selectedList: [] },
      { type: 'car', list: [], selectedList: [] },
    ];
    workResources.forEach((workResource) => {
      const reportWorkResource = {
        ...workResource,
        ...(reportWorkResources.find(({ id }) => id === workResource.id) || { value: 0, text: '' }),
      };
      if (['machinery', 'att', 'car'].includes(workResource.type)) {
        const reportMachinery = {
          ...workResource,
          value: reportWorkResource.value || 0,
        };
        const reportMachineryGroup = reportMachineryGroups.find((item) => item.type === workResource.type);
        reportMachineryGroup?.list.push(reportMachinery);
        if (reportMachinery.value > 0) {
          reportMachineryGroup?.selectedList.push(reportMachinery);
        }
      } else {
        const workResourceType = workResource.type as ReportWorkResourceType;
        const workResourceGroup = reportWorkResourceGroups[workResourceType].find(({ group }) => group === workResource.group);
        if (workResourceGroup) {
          workResourceGroup.list.push(reportWorkResource);
        } else {
          reportWorkResourceGroups[workResourceType].push({ group: workResource.group, list: [reportWorkResource] });
          reportWorkResourceGroups[workResourceType].sort((A, B) => A.group - B.group);
        }
      }
    });
    setWorkResourceGroups(reportWorkResourceGroups);
    setMachineryGroups(reportMachineryGroups);
  }, [report?.workResources, workResources]);

  useEffect(() => {
    (async () => {
      const reportsResponse = await api.report.getsByOwn({
        dateCode,
        sorts: [{ key: 'ledger.startDate', order: sortType.desc }],
        with: ['ledger', 'leader', 'safetyOfficer', 'member', 'resource'],
        forEdit: true,
      });
      const reportsWithAttributes: ReportWithAttributes[] = reportsResponse.map((report) => ({
        ...report,
        workProcessId: report.workProcessId || 1,
        contractType: report.contractType || 1,
        ledgerName: report.ledger?.shortName || '',
        memberUsers: (report.memberUsers || []).map((member) => ({
          ...member,
          status: member.status || 1,
          inTime: member.inDatetime
            ? ((date) => ({ hour: date.getHours(), minute: date.getMinutes() }))(new Date(member.inDatetime * 1000))
            : { ...startTime },
          outTime: member.outDatetime
            ? ((date) => ({ hour: date.getHours(), minute: date.getMinutes() }))(new Date(member.outDatetime * 1000))
            : { ...endTime },
          setInTime: !!member.inDatetime,
          setOutTime: !!member.outDatetime,
        })),
        memberPartners: (report.memberPartners || []).map((member) => ({
          ...member,
          status: member.status || 1,
          inTime: member.inDatetime
            ? ((date) => ({ hour: date.getHours(), minute: date.getMinutes() }))(new Date(member.inDatetime * 1000))
            : { ...startTime },
          outTime: member.outDatetime
            ? ((date) => ({ hour: date.getHours(), minute: date.getMinutes() }))(new Date(member.outDatetime * 1000))
            : { ...endTime },
          setInTime: !!member.inDatetime,
          setOutTime: !!member.outDatetime,
          count: report.workProcessId ? member.count : 1,
        })),
      }));
      const targetReportIndex = reportIndex < 0
        ? Math.max(reportsWithAttributes.findIndex(({ id }) => id === initialReportId), 0)
        : 0;
      reportsWithAttributes.length && onSelectReport(reportsWithAttributes[targetReportIndex], false);
      setReports(reportsWithAttributes);
      setReportIndex(targetReportIndex);
      setTargetDate(new Date(
        Math.floor(dateCode / 10000),
        Math.floor(dateCode % 10000 / 100 - 1),
        dateCode % 100
      ));
      setError({});
    })();
  }, [dateCode]);

  useEffect(() => {
    const enableSetPast = !!owner?.role?.permissions.reportEdit;
    if (!enableSetPast) {
      const pastDate = new Date();
      pastDate.setDate(pastDate.getDate() - 3);
      setMinDate(pastDate);
    }
  }, [owner]);

  return <Layout>
    <BackLink link='/report/list' label='一覧'></BackLink>
    <div css={style.layout}>
      <div>
        <H2>日報作成</H2>
        <div css={generalStyle.form}>
          <FormContent label='作業日' required={true}>
            <div css={generalStyle.formRow}>
              <SelectDate
                onChange={onChangeTargetDate}
                selected={targetDate}
                minDate={minDate}
              />
              {!!reports.length && (
                <Select
                  list={reports}
                  onChange={(data) => data && onSelectReport(data)}
                  value={report}
                  labelKey='ledgerName'
                />
              )}
            </div>
          </FormContent>
          {reports.length ? (
            <Fragment>
              <div data-index='time'>
                <ErrorMessage message={error.time} />
                <div css={style.dateArea}>
                  <div css={style.timeArea}>
                    <FormContent label='開始時間' required={true}>
                      <SelectTime value={startTime} onChange={onChangeStartTime} />
                    </FormContent>
                    <FormContent label='終了時間' required={true}>
                      <SelectTime value={endTime} onChange={onChangeEndTime} />
                    </FormContent>
                  </div>
                </div>
              </div>
              <FormContent label='現場種別' required={true} anchorCode='contractType'>
                <ErrorMessage message={error.contractType} />
                <div css={style.inputArea}>
                  <RadioList
                    list={reportContractTypeList}
                    value={contractType}
                    labelKey='label'
                    onChange={(item) => onChangeReport('contractType', item.value)}
                  />
                </div>
              </FormContent>
              <FormContent label='作業内容' required={true} anchorCode='workProcessId'>
                <ErrorMessage message={error.workProcessId} />
                <Select list={workProcesses} value={workProcess} labelKey='name' onChange={(item) => onChangeReport('workProcessId', item?.id || 0)} />
              </FormContent>
              <FormContent label='作業内容詳細'>
                <textarea value={report?.summary || ''} onChange={(e) => onChangeReport('summary', e.target.value)} placeholder='作業内容詳細' css={style.option}></textarea>
              </FormContent>
              {uploadedResources.length > 0 && (
                <FormContent
                  label={`アップロード済み作業写真 (${uploadedResources.length})`}
                  css={style.uploadedResourceArea(displayUploadedResources)}
                  onClick={() => setDisplayUploadedResources(!displayUploadedResources)}
                >
                  <div css={style.resourceArea}>
                    {uploadedResources.map((resourceParam, index) => (
                      <div css={style.resourceItem(resourceParam.delete)} key={index}>
                        <button onClick={() => onDeleteUploadedReource(index)} css={style.addButton(resourceParam.delete)}>ー</button>
                        <div>{resourceParam.originalName || 'ー'}</div>
                        <div>{resourceParam.detail?.summary || ''}</div>
                        {resourceParam.thumbnailName ? (
                          <img src={`${resourceUrl}/C1/${report?.resourceCode || ''}/${resourceParam.thumbnailName}`} />
                        ) : (
                          <div />
                        )}
                      </div>
                    ))}
                  </div>
                </FormContent>
              )}
              <FormContent label='作業写真' required={!uploadedResources.length} anchorCode='resource'>
                <ErrorMessage message={error.resource} />
                <div css={style.resourceArea}>
                  {uploadResources.map((resourceParam, index) => (
                    <div css={style.resourceItem()} key={index}>
                      <button onClick={() => onDeleteUploadReource(index)} css={style.addButton()}>ー</button>
                      <div>{resourceParam.file?.name || 'ー'}</div>
                      <input
                        type='text'
                        value={resourceParam.summary}
                        onChange={(event) => changeUploadResourceSummary(index, event.target.value)}
                        placeholder='備考説明'
                      />
                      {resourceParam.thumbnail ? (
                        <img src={resourceParam.thumbnail} />
                      ) : (
                        <div />
                      )}
                    </div>
                  ))}
                  <input
                    type='file'
                    multiple
                    hidden
                    accept='image/*,.png,.jpg,.jpeg,.gif'
                    onChange={onAddUploadResource}
                    ref={resourceInputRef}
                  />
                  <button onClick={() => resourceInputRef.current?.click()} css={style.addButton()}>＋ファイル追加</button>
                </div>
              </FormContent>
              {(report?.memberUsers || []).map((user, userIndex) => (
                <div css={style.memberOperation} key={`user.${userIndex}`}>
                  <div css={style.memberOperationPart}>
                    <FormContent label={`作業員氏名${userIndex + 1}`}>
                      {user.isGuest ? (
                        <div css={style.inputArea}>
                          <button onClick={() => onRemoveGuestUser(userIndex)} css={style.addButton()}>ー</button>
                          <SelectUser
                            list={[...(user.id >= 0 ? [user] : []), ...guestUserCandidates]}
                            value={user.id >= 0 ? user : undefined}
                            onChange={(value) => value && onChangeGuestUser(userIndex, value)}
                            css={style.memberSelect}
                          />
                        </div>
                      ) : (
                        <div>{convertUserName(user)}</div>
                      )}
                    </FormContent>
                    <FormContent label='勤怠ステータス'>
                      <div css={style.inputArea}>
                        <RadioList
                          list={[...reportMemberStatusList]}
                          value={reportMemberStatusList.find(({ value }) => value === (user.status || 1))}
                          labelKey='label'
                          onChange={(status) => onChangeMemberUser(user, 'status', status.value)}
                        />
                      </div>
                    </FormContent>
                  </div>
                  <div css={style.memberOperationPart}>
                    <FormContent label='開始時間'>
                      <div css={style.inputArea}>
                        <input
                          type='checkbox'
                          checked={user.setInTime}
                          onChange={(event) => onChangeMemberUser(user, 'setInTime', event.target.checked)}
                          disabled={user.status === 2}
                        />
                        <SelectTime
                          value={user.inTime}
                          onChange={(type, value) => onChangeMemberUser(user, 'inTime', { ...user.inTime, [type]: value })}
                          disabled={user.status === 2 || !user.setInTime}
                        />
                      </div>
                    </FormContent>
                    <FormContent label='終了時間'>
                      <div css={style.inputArea}>
                        <input
                          type='checkbox'
                          checked={user.setOutTime}
                          onChange={(event) => onChangeMemberUser(user, 'setOutTime', event.target.checked)}
                          disabled={user.status === 2}
                        />
                        <SelectTime
                          value={user.outTime}
                          onChange={(type, value) => onChangeMemberUser(user, 'outTime', { ...user.outTime, [type]: value })}
                          disabled={user.status === 2 || !user.setOutTime}
                        />
                      </div>
                    </FormContent>
                  </div>
                </div>
              ))}
              <button onClick={onAddGuestUser} css={style.addButton()}>＋作業員追加</button>
              {(report?.memberPartners || []).map((partner, partnerIndex) => (
                <div css={style.memberOperation} key={`partner.${partnerIndex}`}>
                  <div css={style.memberOperationPart}>
                    <FormContent label={`業者名${partnerIndex + 1}`}>
                      {partner.isGuest ? (
                        <div css={style.inputArea}>
                          <button onClick={() => onRemoveGuestPartner(partnerIndex)} css={style.addButton()}>ー</button>
                          <Select
                            list={[...(partner.id >= 0 ? [partner] : []), ...guestPartnerCandidates]}
                            value={partner.id >= 0 ? partner : undefined}
                            labelKey='name'
                            onChange={(value) => value && onChangeGuestPartner(partnerIndex, value)}
                          />
                        </div>
                      ) : (
                        <div>{partner.name || ''}</div>
                      )}
                    </FormContent>
                    <FormContent label='人数'>
                      <Select
                        list={partnerCandidates}
                        labelKey='label'
                        value={partnerCandidates.find(({ value }) => value === partner.count) || partnerCandidates[1]}
                        onChange={(item) => onChangeMemberPartner(partner, 'count', item?.value || 0)}
                      />
                    </FormContent>
                  </div>
                  <div css={style.memberOperationPart}>
                    <FormContent label='開始時間'>
                      <div css={style.inputArea}>
                        <input
                          type='checkbox'
                          checked={partner.setInTime}
                          onChange={(event) => onChangeMemberPartner(partner, 'setInTime', event.target.checked)}
                          disabled={partner.count === 0}
                        />
                        <SelectTime
                          value={partner.inTime}
                          onChange={(type, value) => onChangeMemberPartner(partner, 'inTime', { ...partner.inTime, [type]: value })}
                          disabled={partner.count === 0 || !partner.setInTime}
                        />
                      </div>
                    </FormContent>
                    <FormContent label='終了時間'>
                      <div css={style.inputArea}>
                        <input
                          type='checkbox'
                          checked={partner.setOutTime}
                          onChange={(event) => onChangeMemberPartner(partner, 'setOutTime', event.target.checked)}
                          disabled={partner.count === 0}
                        />
                        <SelectTime
                          value={partner.outTime}
                          onChange={(type, value) => onChangeMemberPartner(partner, 'outTime', { ...partner.outTime, [type]: value })}
                          disabled={partner.count === 0 || !partner.setOutTime}
                        />
                      </div>
                    </FormContent>
                  </div>
                </div>
              ))}
              <button onClick={onAddGuestPartner} css={style.addButton()}>＋協力業者追加</button>
              <WorkResourceGroupForm groups={workResourceGroups} groupCode='person' onChange={onChangeWorkResource} />
              <WorkResourceGroupForm groups={workResourceGroups} groupCode='tool' onChange={onChangeWorkResource} />
              {reportMachineryGroups.map(({ type, selectedList, candidateList }) => (
                <div css={generalStyle.form} key={type}>
                  {selectedList.map((machinery, index) => (
                    <div css={style.machineryOperation} key={index}>
                      <FormContent label={`${machineryLabel[type]}${index + 1}`}>
                        <div css={style.inputArea}>
                          <button onClick={() => onRemoveMachinery(type, index)} css={style.addButton()}>ー</button>
                          <Select
                            list={[...(machinery.id >= 0 ? [machinery] : []), ...candidateList]}
                            value={machinery.id >= 0 ? machinery : undefined}
                            labelKey='name'
                            onChange={(item) => onChangeMachinery(type, index, item?.id || 0)}
                            css={style.machinerySelect}
                          />
                        </div>
                      </FormContent>
                      <FormContent label='台数'>
                        <Select
                          list={machineryCandidates}
                          labelKey='value'
                          value={machineryCandidates.find(({ value }) => value === machinery.value) || machineryCandidates[0]}
                          onChange={(item) => onChangeMachineryValue(machinery, item?.value || 0)}
                        />
                      </FormContent>
                    </div>
                  ))}
                  <button onClick={() => onAddMachinery(type)} css={style.addButton()}>{`＋${machineryLabel[type]}追加`}</button>
                </div>
              ))}
              <WorkResourceGroupForm groups={workResourceGroups} groupCode='car_related' onChange={onChangeWorkResource} />
              <WorkResourceGroupForm groups={workResourceGroups} groupCode='industrial_waste' onChange={onChangeWorkResource} />
              <WorkResourceGroupForm groups={workResourceGroups} groupCode='other' onChange={onChangeWorkResource} />
              <div css={generalStyle.formRow}>
                <button onClick={() => setShowSaveConfirm(true)} css={generalStyle.button('info')}>下書き保存</button>
                <button onClick={onClickSubmitButton} css={generalStyle.button()}>報告</button>
              </div>
            </Fragment>
          ) : (
            <div>作業予定がありません</div>
          )}
        </div>
      </div>
      <div css={style.informationArea}>
        {!!reports.length && (
          <Fragment>
            <div>
              <div css={style.informationLabel}>現場名略称</div>
              <div css={style.informationValue}>{report?.ledgerName || ''}</div>
            </div>
            <div>
              <div css={style.informationLabel}>現場代理人</div>
              <div css={style.informationValue}>{report?.leader?.lastName || ''} {report?.leader?.firstName || ''}</div>
            </div>
            <div>
              <div css={style.informationLabel}>上長</div>
              <div css={style.informationValue}>{report?.safetyOfficer?.lastName || ''} {report?.safetyOfficer?.firstName || ''}</div>
            </div>
            <div>
              <div css={style.informationLabel}>元請け名</div>
              <div css={style.informationValue}>{report?.ledger?.orderingParty || 'ー'}</div>
            </div>
          </Fragment>
        )}
      </div>
    </div>
    <ConfirmResultModal
      display={showSaveConfirm}
      onHide={() => setShowSaveConfirm(false)}
      onSubmit={onSave}
      objectType='report'
      operationType='save'
    />
    <ConfirmResultModal
      display={showSubmitConfirm}
      onHide={() => setShowSubmitConfirm(false)}
      onSubmit={onSubmitReport}
      onOk={() => navigate('/report/list')}
      objectType='report'
      operationType='submit'
    />
  </Layout>;
};

export default ReportEdit;
